import React, { useState } from 'react';
import SearchableSelect from './SearchableSelect';

/**
 * Disciplinas do Polivalente — seção do cadastro da turma.
 *
 * Na grade o Polivalente é uma disciplina só, mas a nota é por componente: a
 * aba Notas do Diário mostra, com o Polivalente selecionado, as disciplinas
 * em que o professor lança. É aqui que a coordenação escolhe quais são (ver
 * backend/api/polivalente.py).
 *
 * `selecao` é `null` enquanto vale a lista padrão da rede, ou a lista de ids
 * escolhida. Lista vazia também é escolha: nota única, no próprio Polivalente.
 *
 * `onCriarDisciplina(nome)`, quando vem, deixa criar a disciplina que falta no
 * cadastro da rede; resolve com `{ id, subject_name }` já incluída em
 * `config.outras`. Sem ele (quem não cria disciplina), o botão não aparece.
 */
function DisciplinasDoPolivalente({ config, selecao, onChange, onCriarDisciplina, darkMode }) {
  const [criando, setCriando] = useState(false);
  const [novoNome, setNovoNome] = useState('');
  const [erroNova, setErroNova] = useState('');
  const [salvandoNova, setSalvandoNova] = useState(false);

  const idsPadrao = config.opcoes.filter((o) => o.padrao).map((o) => o.id);
  const marcadas = new Set(selecao === null ? idsPadrao : selecao);

  // Disciplina acrescentada agora ainda não está nas opções que o backend
  // mandou: ela vem de `outras` e aparece como as demais.
  const opcoes = [
    ...config.opcoes,
    ...config.outras.filter((o) => marcadas.has(o.id)),
  ];
  const disponiveis = config.outras.filter((o) => !marcadas.has(o.id));

  const especialistas = opcoes.filter((o) => o.professor_proprio);
  const lancadas = opcoes.filter((o) => marcadas.has(o.id) && !o.professor_proprio);
  // Notas que já existem e saem da aba Notas do professor ao salvar.
  const saemComNota = opcoes.filter(
    (o) => o.marcada && !marcadas.has(o.id) && !o.professor_proprio && o.alunos_com_nota > 0
  );

  const alternar = (id) => {
    const atual = selecao === null ? idsPadrao : selecao;
    onChange(atual.includes(id) ? atual.filter((x) => x !== id) : [...atual, id]);
  };

  const acrescentar = (valor) => {
    if (!valor) return;
    const atual = selecao === null ? idsPadrao : selecao;
    onChange([...atual, Number(valor)]);
  };

  const criar = async () => {
    const nome = novoNome.trim();
    if (!nome || salvandoNova) return;
    setSalvandoNova(true);
    setErroNova('');
    try {
      const nova = await onCriarDisciplina(nome);
      acrescentar(nova.id);
      setNovoNome('');
      setCriando(false);
    } catch (err) {
      setErroNova(err.message || 'Não foi possível criar a disciplina.');
    } finally {
      setSalvandoNova(false);
    }
  };

  const muted = darkMode ? 'text-slate-400' : 'text-slate-500';

  return (
    <div className={`rounded-xl p-4 border ${darkMode ? 'border-slate-700 bg-slate-900/30' : 'border-slate-200 bg-slate-50'}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <p className={`text-sm font-medium ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
          Disciplinas do Polivalente
        </p>
        <span className={`text-[11px] px-2 py-0.5 rounded-full ${selecao === null
          ? darkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-200 text-slate-600'
          : 'bg-[#0974BA]/15 text-[#0974BA]'
        }`}>
          {selecao === null ? 'Lista padrão' : 'Escolhida pela coordenação'}
        </span>
      </div>
      <p className={`text-xs mb-3 ${muted}`}>
        Em quais disciplinas o professor do Polivalente lança nota nesta turma
        (aba Notas do Diário).
      </p>

      <div className="flex flex-wrap gap-2">
        {opcoes.map((o) => {
          if (o.professor_proprio) {
            return (
              <span
                key={o.id}
                title={`Tem professor próprio nesta turma (${o.professor_proprio}): as notas são dele.`}
                className={`px-3 py-1.5 rounded-lg text-sm border border-dashed cursor-not-allowed opacity-60 ${darkMode
                  ? 'border-slate-600 text-slate-400'
                  : 'border-slate-300 text-slate-500'
                }`}
              >
                🔒 {o.subject_name}
              </span>
            );
          }
          const ativa = marcadas.has(o.id);
          return (
            <button
              key={o.id}
              type="button"
              aria-pressed={ativa}
              onClick={() => alternar(o.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${ativa
                ? 'text-white border-transparent shadow'
                : darkMode
                  ? 'border-slate-600 text-slate-400 hover:border-slate-500'
                  : 'border-slate-300 text-slate-500 hover:border-slate-400'
              }`}
              style={ativa ? { background: 'linear-gradient(135deg, #0974BA, #2E4974)' } : {}}
            >
              {ativa ? '✓ ' : ''}{o.subject_name}
            </button>
          );
        })}
      </div>

      {disponiveis.length > 0 && (
        <div className="mt-3">
          <SearchableSelect
            value=""
            onChange={acrescentar}
            options={disponiveis.map((o) => ({ value: o.id, label: o.subject_name }))}
            placeholder="+ Acrescentar outra disciplina"
            searchPlaceholder="Buscar disciplina..."
            darkMode={darkMode}
            className="w-full"
          />
        </div>
      )}

      {onCriarDisciplina && (criando ? (
        <div className="mt-3">
          <div className="flex gap-2">
            <input
              type="text"
              value={novoNome}
              autoFocus
              maxLength={100}
              onChange={(e) => setNovoNome(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); criar(); }
                if (e.key === 'Escape') { e.preventDefault(); setCriando(false); setErroNova(''); }
              }}
              placeholder="Nome da nova disciplina (ex.: Inglês)"
              className={`flex-1 min-w-0 px-3 py-2 rounded-lg text-sm border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${darkMode
                ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                : 'bg-white border-slate-200 text-slate-700'
              }`}
            />
            <button
              type="button"
              onClick={criar}
              disabled={!novoNome.trim() || salvandoNova}
              className="px-3 py-2 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-[#0974BA] to-[#2E4974] disabled:opacity-50"
            >
              {salvandoNova ? 'Criando...' : 'Criar'}
            </button>
            <button
              type="button"
              onClick={() => { setCriando(false); setErroNova(''); }}
              className={`px-3 py-2 rounded-lg text-sm ${darkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700'}`}
            >
              Cancelar
            </button>
          </div>
          {erroNova ? (
            <p className="mt-1 text-xs text-[#E83A3C]">{erroNova}</p>
          ) : (
            <p className={`mt-1 text-xs ${muted}`}>
              A disciplina entra no cadastro de disciplinas da rede e já fica
              marcada nesta turma.
            </p>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setCriando(true)}
          className="mt-2 text-xs font-medium text-[#0974BA] hover:underline"
        >
          + Nova disciplina (não está na lista)
        </button>
      ))}

      <div className={`mt-3 space-y-1 text-xs ${muted}`}>
        {lancadas.length === 0 && (
          <p>
            Nenhuma disciplina marcada: o professor lança uma nota só, no
            próprio Polivalente.
          </p>
        )}
        {especialistas.length > 0 && (
          <p>
            🔒 Com professor próprio nesta turma, que lança as notas:{' '}
            {especialistas.map((o) => `${o.subject_name} (${o.professor_proprio})`).join(', ')}.
          </p>
        )}
        {!config.tem_polivalente && (
          <p>
            A turma não tem Polivalente na grade horária agora; a escolha vale
            quando tiver.
          </p>
        )}
        {saemComNota.map((o) => (
          <p key={o.id} className={darkMode ? 'text-[#F9B047]' : 'text-[#B87503]'}>
            ⚠ {o.subject_name} já tem nota de {o.alunos_com_nota} aluno(s) nesta
            turma. As notas continuam gravadas, mas saem da aba Notas do
            professor do Polivalente.
          </p>
        ))}
      </div>

      {selecao !== null && (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="mt-3 text-xs font-medium text-[#0974BA] hover:underline"
        >
          Voltar para a lista padrão
        </button>
      )}
    </div>
  );
}

export default DisciplinasDoPolivalente;
